import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import { useFormik } from 'formik';
import moment from 'moment';
import 'moment-timezone';
import { DeleteButton, SaveButton } from '~components/action-buttons';
import { PageTopBar } from '~components/page-top-bar';
import {
    useApplicationSelectionStore,
    useAuthStore,
    useFeaturedStore,
    useGamesStore,
    usePromotionStore,
} from '~contexts/mobx';
import { useUnsavedChanges } from '~contexts/unsaved-changes';
import {
    FeaturedForm as FeaturedFormValues,
    yupFeaturedForm,
} from '~stores/featured';
import { isLegacyFeaturedType } from '~utils/const';
import {
    convertLocalToUtcString,
    convertUtcToLocalMoment,
    getUserTimezoneOrFallback,
} from '~utils/timezone';
import { useGenericParams } from '~utils/use-generic-params';
import { useNavigation } from '~utils/use-navigation';
import { usePlacementLinking } from '~utils/use-placement-linking';
import {
    isGameEligibleForFeaturedCreate,
    isPromotionEligibleForFeaturedCreate,
} from './utils/province-filter';
import { FeaturedForm } from './components/FeaturedForm';

type Form = FeaturedFormValues;

const externalUrlValidation = (values: Form) => {
    if (values.type !== 'external_url') {
        return true;
    }
    return values.items[0].externalUrl && values.items[1].externalUrl;
};

const convertDatesToUtc = (values: Form) => {
    return {
        ...values,
        startDate: convertLocalToUtcString(values.startDate, values.timezone),
        endDate: convertLocalToUtcString(values.endDate, values.timezone),
        appTarget: values.type === 'external_url' ? values.appTarget : null,
    };
};

export const FeaturedSinglePage = observer(() => {
    const promotionStore = usePromotionStore();
    const gamesStore = useGamesStore();
    const featuredStore = useFeaturedStore();
    const authStore = useAuthStore();
    const applicationSelectionStore = useApplicationSelectionStore();
    const navigation = useNavigation();
    const { redirectAfterCreate } = usePlacementLinking();
    const [isUpserting, setIsUpserting] = React.useState<boolean>(false);
    const [isFetching, setIsFetching] = React.useState<boolean>(false);
    const [isDeleting, setIsDeleting] = React.useState<boolean>(false);
    const params = useGenericParams(() =>
        navigation.replaceToFeaturedCreation()
    );

    const { hasManagementAccess } = authStore;

    if (!hasManagementAccess) {
        navigation.goToHome();
    }

    const formik = useFormik<Form>({
        initialValues: {
            id: 0,
            type: 'promotion',
            isActive: true,
            canBeDeleted: false,
            geolocated: null,
            appTarget: null,
            isImageTitle: false,
            gameTypeId: null,
            gameId: null,
            gameType: null,
            startDate: moment().startOf('day'),
            endDate: moment().endOf('day'),
            timezone: getUserTimezoneOrFallback(),
            items: [
                {
                    backgroundImage: '',
                    imageTitle: '',
                    textTitle: '',
                    title: '',
                    subTitle: '',
                    textColor: null,
                    backgroundColor: null,
                    titleColor: null,
                    subtitleColor: null,
                    buttonColor: null,
                    cta: '',
                    externalUrl: '',
                    language: 'en',
                },
                {
                    backgroundImage: '',
                    imageTitle: '',
                    textTitle: '',
                    title: '',
                    subTitle: '',
                    textColor: null,
                    backgroundColor: null,
                    titleColor: null,
                    subtitleColor: null,
                    buttonColor: null,
                    cta: '',
                    externalUrl: '',
                    language: 'fr',
                },
            ],
        },
        validateOnMount: true,
        validationSchema: yupFeaturedForm,
        onSubmit: () => console.log('submit'),
    });
    const resetFeaturedForm = formik.resetForm;
    const setFeaturedFieldValue = formik.setFieldValue;
    const selectedFeaturedType = formik.values.type;
    const selectedPromotionId = formik.values.promotionId;
    const selectedGameTypeId = formik.values.gameTypeId;

    const saveFeatured = useCallback(async () => {
        setIsUpserting(true);
        try {
            const currentValues = formik.values;
            const data = convertDatesToUtc(currentValues);
            if (params.action === 'edit') {
                await featuredStore.updateFeatured(Number(params.id), data);
            } else {
                await featuredStore.createFeatured(data);
            }

            resetFeaturedForm({ values: currentValues });
        } finally {
            setIsUpserting(false);
        }
    }, [
        featuredStore,
        formik.values,
        params.action,
        params.id,
        resetFeaturedForm,
    ]);

    const handleSave = useCallback(async () => {
        await saveFeatured();
    }, [saveFeatured]);

    const { clearDirty } = useUnsavedChanges({
        dirty: formik.dirty,
        onSave: handleSave,
    });

    const createFeatured = useCallback(async () => {
        await saveFeatured();

        clearDirty(() =>
            redirectAfterCreate(() => navigation.replaceToFeaturedList())
        );
    }, [clearDirty, navigation, redirectAfterCreate, saveFeatured]);

    const updateFeatured = useCallback(async () => {
        await saveFeatured();
        clearDirty();
    }, [clearDirty, saveFeatured]);

    React.useEffect(() => {
        if (params.action !== 'edit') {
            return;
        }
        if (!params.id) {
            return;
        }

        let isCancelled = false;
        setIsFetching(true);
        featuredStore
            .fetchFeatured(Number(params.id))
            .then((data) => {
                if (isCancelled) {
                    return;
                }
                if (!data) {
                    navigation.replaceToFeaturedCreation();
                    return;
                }

                const hasBackgroundColor = data.items.some(
                    (item) => item.backgroundColor !== null
                );
                const normalizedItems = hasBackgroundColor
                    ? data.items.map((item) => ({
                          ...item,
                          textTitle: null,
                          imageTitle: null,
                          textColor: null,
                      }))
                    : data.items;

                resetFeaturedForm({
                    values: {
                        ...data,
                        items: normalizedItems,
                        startDate: convertUtcToLocalMoment(
                            data.startDate,
                            data.timezone
                        ),
                        endDate: convertUtcToLocalMoment(
                            data.endDate,
                            data.timezone
                        ),
                        isImageTitle: hasBackgroundColor
                            ? false
                            : data.items[0].imageTitle !== '',
                        geolocated: hasBackgroundColor ? null : data.geolocated,
                        appTarget: data.appTarget ?? null,
                        gameTypeId: `${data.gameType}_${data.gameId}`,
                    },
                });
            })
            .catch(() => {
                if (!isCancelled) {
                    navigation.replaceToFeaturedCreation();
                }
            })
            .finally(() => {
                if (!isCancelled) {
                    setIsFetching(false);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [
        featuredStore,
        navigation,
        params.action,
        params.id,
        resetFeaturedForm,
    ]);

    React.useEffect(() => {
        promotionStore.fetchForSelect();
    }, [applicationSelectionStore.selectedApplication, promotionStore]);

    React.useEffect(() => {
        gamesStore.fetchForSelect();
    }, [applicationSelectionStore.selectedApplication, gamesStore]);

    const isCreateMode = params.action === 'create';
    const appTarget = applicationSelectionStore.appTarget;

    const initialAppTargetRef = React.useRef(appTarget);
    React.useEffect(() => {
        if (appTarget !== initialAppTargetRef.current) {
            navigation.replaceToFeaturedList();
        }
    }, [appTarget, navigation]);

    const promoList = React.useMemo(() => {
        const activePromotions = promotionStore.promotionsForSelect.filter(
            (promo) => promo.isActive === true
        );

        return activePromotions.filter((promo) =>
            isPromotionEligibleForFeaturedCreate(promo, appTarget)
        );
    }, [appTarget, promotionStore.promotionsForSelect]);

    const gameList = React.useMemo(() => {
        return gamesStore.gamesForSelect.filter((game) =>
            isGameEligibleForFeaturedCreate(game, appTarget)
        );
    }, [appTarget, gamesStore.gamesForSelect]);

    React.useEffect(() => {
        if (!isCreateMode) {
            return;
        }

        if (
            selectedFeaturedType === 'promotion' &&
            selectedPromotionId &&
            !promoList.some((promo) => promo.id === selectedPromotionId)
        ) {
            setFeaturedFieldValue('promotionId', null);
        }

        if (
            selectedFeaturedType === 'games' &&
            selectedGameTypeId &&
            !gameList.some(
                (game) => `${game.type}_${game.id}` === selectedGameTypeId
            )
        ) {
            setFeaturedFieldValue('gameTypeId', null);
            setFeaturedFieldValue('gameType', null);
            setFeaturedFieldValue('gameId', null);
        }
    }, [
        gameList,
        isCreateMode,
        promoList,
        selectedFeaturedType,
        selectedGameTypeId,
        selectedPromotionId,
        setFeaturedFieldValue,
    ]);

    const onDeleteConfirm = async () => {
        setIsDeleting(true);
        const response = await featuredStore.deleteFeatured(Number(params.id));
        setIsDeleting(false);
        if (!response.err) {
            navigation.replaceToFeaturedList();
        }
    };

    const renderTopBarActions = () => {
        const isLegacy = isLegacyFeaturedType(formik.values.type);
        const isValid = formik.isValid && externalUrlValidation(formik.values);
        const isEditSaveDisabled = !isValid || !formik.dirty || isLegacy;

        switch (params.action) {
            case 'create':
                return (
                    <SaveButton
                        onClick={createFeatured}
                        loading={isUpserting}
                        disabled={!isValid}
                    />
                );
            case 'edit':
                return (
                    <>
                        {formik.values.canBeDeleted && (
                            <DeleteButton
                                onClick={onDeleteConfirm}
                                loading={isDeleting}
                            />
                        )}
                        <SaveButton
                            onClick={updateFeatured}
                            loading={isUpserting}
                            disabled={isEditSaveDisabled}
                        />
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <PageTopBar
                title={
                    formik.values.id !== 0
                        ? `Featured #${formik.values.id}`
                        : 'New Featured'
                }
                goBack={{
                    callback: () => navigation.goToFeaturedList(),
                    text: 'Back to list',
                }}
            >
                {renderTopBarActions()}
            </PageTopBar>

            <FeaturedForm
                action={params.action}
                formik={formik}
                isFetching={isFetching}
                promotionsForSelect={promoList}
                promotionsLoading={promotionStore.isFetchingForSelect}
                gamesForSelect={gameList}
                gamesLoading={gamesStore.isFetchingForSelect}
            />
        </>
    );
});
